import React from 'react';
import { Link } from 'react-router-dom';
import * as wasm from 'sched';
import type { WorkspaceUrlTarget } from '@/routes/workspaceUrl';

type BuildUrl = (target: WorkspaceUrlTarget) => string;

// ---------------------------------------------------------------------------
// CreateItem → "New {Type} created: {linkable title}"
// ---------------------------------------------------------------------------

interface CreateInfo {
  /** Lower-cased noun used in the toast prefix, e.g. "task". */
  entityLabel: string;
  /** Title or name extracted from the new entity. May be empty. */
  title: string;
  /** Detail-page target, or `null` for entity types that have no detail page. */
  target: WorkspaceUrlTarget | null;
}

/**
 * Inspect the item value to find a human-readable title or name.
 *
 * The shapes vary across tsify-generated entity types:
 *   - Task uses an externally-tagged enum: `{ Native: { title } } | { Remote: { … } }`
 *   - Outcome uses an internally-tagged enum: `{ type: "Native", title, … }`
 *   - Other entities are plain structs with a `title` or `name` field
 *
 * This walks the variant indirection, then reads `title || name`.
 */
function extractTitle(value: any): string {
  if (!value || typeof value !== 'object') return '';
  if ('Native' in value && value.Native) return extractTitle(value.Native);
  if ('Remote' in value && value.Remote) return extractTitle(value.Remote);
  if (value.type === 'Native' || value.type === 'Remote') {
    const { type: _t, ...rest } = value;
    return extractTitle(rest);
  }
  return value.title ?? value.name ?? '';
}

function describeCreatedItem(command: { id: string; item: { type: string; value: any } }): CreateInfo {
  const id = command.id;
  const title = extractTitle(command.item.value);
  switch (command.item.type) {
    case 'Task':
      return { entityLabel: 'task', title, target: { kind: 'task', taskId: id } };
    case 'Outcome':
      return { entityLabel: 'outcome', title, target: { kind: 'outcome', outcomeId: id } };
    case 'Team':
      return { entityLabel: 'team', title, target: { kind: 'team', teamId: id } };
    case 'Person':
      return { entityLabel: 'person', title, target: { kind: 'person', personId: id } };
    case 'Decision':
      return { entityLabel: 'decision', title, target: { kind: 'decision', decisionId: id } };
    case 'Problem':
      return { entityLabel: 'problem', title, target: { kind: 'problem', problemId: id } };
    case 'Stakeholder':
      return { entityLabel: 'stakeholder', title, target: { kind: 'stakeholder', stakeholderId: id } };
    case 'Product':
      return { entityLabel: 'product', title, target: { kind: 'product', productId: id } };
    // Some forms send `type: "Project"` to populate the same backing enum
    // that the WASM type calls `Product`. Route both to the project URL —
    // this matches existing UI behaviour where products and projects share
    // the same detail page.
    case 'Project':
      return { entityLabel: 'project', title, target: { kind: 'project', projectId: id } };
    case 'WorkflowInstance':
      return {
        entityLabel: 'workflow instance',
        title,
        target: { kind: 'workflow-instance', instanceId: id },
      };
    case 'Resource':
      return { entityLabel: 'resource', title, target: { kind: 'resource', resourceId: id } };
    case 'ResourceGroup':
      return {
        entityLabel: 'resource group',
        title,
        target: { kind: 'resource-group', groupId: id },
      };
    case 'ExternalEvent':
      return { entityLabel: 'external event', title, target: null };
    default:
      return { entityLabel: 'item', title, target: null };
  }
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export function commandSuccessMessage(
  command: wasm.DatabaseCommand,
  buildUrl: BuildUrl,
): React.ReactNode {
  // The TS union for `DatabaseCommand` doesn't expose all shapes uniformly,
  // so we narrow on `type` via `any`. Each branch reads only the fields it
  // knows are present for that variant.
  const cmd = command as any;
  switch (cmd.type) {
    case 'CreateItem': {
      const { entityLabel, title, target } = describeCreatedItem(cmd);
      const prefix = `New ${titleCase(entityLabel)} created`;
      const titleText = title.trim() || `(untitled ${entityLabel})`;
      if (target) {
        return (
          <span>
            {prefix}:{' '}
            <Link to={buildUrl(target)} className="underline font-medium">
              {titleText}
            </Link>
          </span>
        );
      }
      return `${prefix}: ${titleText}`;
    }
    case 'UpdateTask':
      return 'Task updated';
    case 'DeleteItem':
      return 'Item deleted successfully';
    case 'IncrementTaskResourceAllocation':
      return 'Resource allocation updated';
    case 'DeleteTaskResourceAllocation':
      return 'Resource allocation deleted';
    default:
      return 'Operation completed successfully';
  }
}

export function commandErrorMessage(command: wasm.DatabaseCommand, error: any): string {
  const cmd = command as any;
  const errorMessage = error?.message || 'Unknown error occurred';
  switch (cmd.type) {
    case 'UpdateTask':
      return 'Failed to update task';
    case 'CreateItem':
      return 'Failed to create item';
    case 'DeleteItem':
      return 'Failed to delete item';
    case 'IncrementTaskResourceAllocation':
      return 'Failed to update resource allocation';
    case 'DeleteTaskResourceAllocation':
      return 'Failed to delete resource allocation';
    default:
      return `Operation failed: ${errorMessage}`;
  }
}
